import React, { useEffect, useMemo, useState } from "react";
import { Grey_Link, IconSvg, Input_, LimitCharacters, Main_, Main_Plain, PageFull, PageTitle, Partial_Loader, Slider_, device } from "monica-alexandria";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import NoImg from "../../assets/NoImage.png";
import b from "../../services/api/Boards";
import { useCRMContext } from "../../context/CrmContext";
import store from "../../redux/store";
import CreateBoardPanel from './CreateBoardPanel';
import { useCrmBoardCreationAccess } from './useCrmBoardCreationAccess';
import { getBoardAvatarSrc } from '../../utils/boardAvatar';

let minioImgUrl =
  process.env.REACT_APP_MINIO_PROTOCOL +
  process.env.REACT_APP_MINIO_HOST +
  process.env.REACT_APP_MINIO_PORT +
  "/" +
  process.env.REACT_APP_MINIO_DAILYPROFIT_BUCKET;

function AdBoardImage({ image, visualElement, userId, title }) {
  const [attempt, setAttempt] = useState(0);
  const sources = [...new Set([
    image,
    visualElement ? `${minioImgUrl}/${userId}/${visualElement}` : null,
    NoImg,
  ].filter(Boolean))];
  return <img src={sources[Math.min(attempt, sources.length - 1)]} alt={title}
    onError={() => setAttempt(previous => Math.min(previous + 1, sources.length - 1))} />;
}

const isValidEmailAddress = (value = "") => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value).trim());
};

const BoardsWrap = styled.div`
  padding-bottom: 4rem;

  @media ${device.md} {
    padding-bottom:2rem;
  }
`;

const SearchWrap = styled.div`
  margin-bottom: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const SearchInput = styled.input`
  width: 100%;
  border: 1px solid ${(p) => p.theme.low};
  background: ${(p) => p.theme.mid};
  color: ${(p) => p.theme.color};
  border-radius: 1.6rem;
  padding: 1.3rem 1.5rem;
  font-size: 1.55rem;
  box-shadow: ${(p) => p.theme.in};

  &::placeholder {
    color: var(--greyDark);
  }

  &:focus {
    outline: none;
    border-color: rgba(43, 199, 84, 0.55);
  }
`;

const SearchFeedback = styled.div`
  color: ${(p) => (p.error ? "#d64545" : "var(--greyDark)")};

  strong {
    color: ${(p) => p.theme.color};
  }
`;

const SearchResults = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const SearchResultButton = styled.button`
  border: 1px solid ${(p) => p.theme.low};
  background: ${(p) => p.theme.mid};
  color: ${(p) => p.theme.color};
  border-radius: 1.4rem;
  padding: 1.1rem 1.3rem;
  text-align: left;
  cursor: pointer;

  p,
  span {
    display: block;
  }

  p {
    margin-bottom: 0.35rem;
    color: ${(p) => p.theme.color};
  }

  strong {
    color: var(--main);
    font-weight: 700;
  }

  span {
    font-size: 1.4rem;
    color: ${(p) => p.theme.color};
  }
`;

const BoardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;

  @media ${device.md} {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const CardActionButton = styled.button`
  border: 1px solid ${(p) => p.theme.low};
  background: ${(p) => p.theme.background};
  color: ${(p) => p.theme.color};
  border-radius: 999px;
  padding: 0.7rem 1.2rem;
  font-size: 1.35rem;
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    border-color: var(--main);
    color: var(--main);
  }
`;

const CardActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.7rem;
`;

const ShareBadge = styled.div`
  border-radius: 999px;
  padding: 0.7rem 1.2rem;
  font-size: 1.35rem;
  background: rgba(35, 141, 255, 0.08);
  color: var(--main);
  white-space: nowrap;
`;

const ShareList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  margin-top: 1.4rem;
`;

const ShareRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.2rem;
  border-radius: 1.2rem;
  background: ${(p) => p.theme.mid};

  strong,
  span {
    display: block;
  }

  strong {
    font-size: 1.4rem;
    color: ${(p) => p.theme.color};
  }

  span {
    font-size: 1.25rem;
    color: var(--greyDark);
  }
`;

const ShareMessage = styled.p`
  margin: 1rem 0 0;
  font-size: 1.35rem;
  color: ${(p) => {
    if (p.statusType === "warning") return "var(--warning)";
    if (p.statusType === "error") return "var(--error)";
    return "var(--greyDark)";
  }};
`;

const ShareInputState = styled.div`
  .FormGroup input {
    border: 1px solid ${(p) => {
      if (p.statusType === "warning") return "var(--warning)";
      if (p.statusType === "error") return "var(--error)";
      return p.theme.low;
    }};
  }
`;

const BoardsList = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(34rem, 1fr));
  gap: 1.6rem;

  @media ${device.md} {
    grid-template-columns: 1fr;
  }
`;

const BoardCard = styled.button`
  border: 0;
  text-align: left;
  cursor: pointer;
  background: ${(p) => p.theme.mid};
  color: ${(p) => p.theme.color};
  border-radius: 2.2rem;
  padding: 1.6rem;
  display: flex;
  flex-direction: column;
  gap: 1.4rem;
  box-shadow: ${(p) => p.theme.in};
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  min-height: 19rem;

  &:hover {
    transform: translateY(-1px);
    background: ${(p) => p.theme.low};
    box-shadow: 0 16px 34px rgba(15, 23, 42, 0.08);
  }

  @media ${device.md} {
    padding: 1.4rem;
    border-radius: 1.6rem;
    min-height: auto;
  }
`;

const BoardTop = styled.div`
  display: grid;
  grid-template-columns: 7.2rem minmax(0, 1fr) auto;
  align-items: center;
  gap: 1.2rem;

  @media ${device.md} {
    grid-template-columns: 6rem minmax(0, 1fr) auto;
  }
`;

const BoardImage = styled.div`
  width: 7.2rem;
  height: 7.2rem;
  border-radius: 1.6rem;
  overflow: hidden;
  background: ${(p) => p.theme.low};
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  @media ${device.md} {
    width: 6rem;
    height: 6rem;
    border-radius: 1.2rem;
  }
`;

const BoardContent = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
`;

const BoardHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;

  h4 {
    margin: 0;
    font-size: 1.95rem;
    line-height: 1.25;
  }

  p {
    margin: 0;
    color: var(--greyDark);
    font-size: 1.45rem;
  }

  @media ${device.md} {
    h4 {
      font-size: 1.7rem;
    }

    p {
      font-size: 1.4rem;
    }
  }
`;

const BoardArrow = styled.div`
  width: 3.8rem;
  height: 3.8rem;
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(p) => p.theme.background};
  border: 1px solid ${(p) => p.theme.low};
  flex-shrink: 0;

  svg {
    width: 1.4rem;
    fill: ${(p) => p.theme.color};
    opacity: 0.55;
    transform: rotate(180deg);
  }
`;

const StatusRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.8rem;
`;

const StatusPill = styled.div`
  border-radius: 999px;
  padding: 0.7rem 1.1rem;
  font-size: 1.5rem;
  line-height: 1;
  border: 1px solid ${(p) => (p.highlight ? "rgba(43, 199, 84, 0.65)" : p.theme.low)};
  background: ${(p) => (p.highlight ? "rgba(43, 199, 84, 0.08)" : p.theme.background)};
  color: ${(p) => (p.highlight ? "#2bc754" : "var(--greyDark)")};
  white-space: nowrap;

  @media ${device.md} {
    font-size: 1.35rem;
  }
`;

const BoardMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  color: var(--greyDark);
  font-size: 1.45rem;

  span {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
  }

  svg {
    width: 1.4rem;
    fill: currentColor;
    opacity: 0.8;
  }
`;

const EmptyState = styled.section`
  position: relative;
  min-height: 32rem;
  padding: 4.8rem 2.4rem;
  border: 1px dashed ${(p) => p.theme.low};
  border-radius: 3rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2.4rem;
  text-align: center;
  color: var(--greyDark);
  transition: background 0.2s ease, border-color 0.2s ease;

  h2 {
    margin: 0;
    color: ${(p) => p.theme.color};
    font-size: 1.8rem;
    font-weight: 600;
    line-height: 1.4;
  }

  p {
    max-width: 42rem;
    margin: 0;
    font-size: 1.5rem;
    line-height: 1.5;
  }

  &:hover,
  &:focus-within {
    background: ${(p) => p.theme.low};
    border-color: var(--main);
  }

  @media ${device.md} {
    padding: 3.2rem 1.6rem;
    border-radius: 2rem;
  }
`;

const EmptyStateIcon = styled.div`
  width: 6rem;
  height: 6rem;
  border-radius: 50%;
  display: grid;
  place-items: center;
  background: rgba(187, 76, 182, 0.25);
  color: #bb4cb6;

  svg {
    width: 2rem;
    height: 2rem;
    fill: currentColor;
  }
`;

const EmptyStateCreate = styled(Main_Plain)`
  position: static;
  border-radius: inherit;

  /* Extend the native button's click target across the empty-state panel. */
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    border-radius: inherit;
  }

  &:active:not(:disabled) {
    transform: none;
  }

  &:focus-visible::after {
    outline: 2px solid var(--main);
    outline-offset: 3px;
  }
`;

const receivedTodayLabel = "Received Today";

const getQuestColumnEntries = (questValue) => {
  const questColumns = questValue?.columns || {};
  return Object.entries(questColumns).map(([title, count]) => {
    return {
      title,
      count: Number(count || 0),
    };
  });
};

const getIsReceivedTodayColumn = (columnTitle) => {
  return String(columnTitle || "").toLowerCase() === receivedTodayLabel.toLowerCase();
};

const getQuestNewLeads = (questValue) => {
  const todayColumn = getQuestColumnEntries(questValue).find((columnEntry) => {
    return getIsReceivedTodayColumn(columnEntry?.title);
  });

  return Number(todayColumn?.count || 0);
};

const getQuestLeadTotal = (questValue) => {
  return getQuestColumnEntries(questValue)
    .filter((columnEntry) => {
      return !getIsReceivedTodayColumn(columnEntry?.title);
    })
    .reduce((totalCount, columnEntry) => {
      return totalCount + Number(columnEntry?.count || 0);
    }, 0);
};

const getMatchLeadName = (leadValue) => {
  return (
    leadValue?.FullName ||
    leadValue?.fullName ||
    leadValue?.fullname ||
    leadValue?.name ||
    [leadValue?.Name, leadValue?.Surname].filter(Boolean).join(" ") ||
    ""
  );
};

const getMatchLeadEmail = (leadValue) => {
  return leadValue?.Email || leadValue?.email || "";
};

const getMatchLeadPhone = (leadValue) => {
  return leadValue?.Tel || leadValue?.tel || "";
};

export default function CRM_BOARDS() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { lang } = useParams();
  const { quests, setQuests, loading, loadError, availabilityWarning, refreshBoards } = useCRMContext();
  const currentUserId = store.getState()?.user?.user?.sub;
  const [searchTerm, setSearchTerm] = useState("");
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchError, setSearchError] = useState(false);
  const [sharingQuest, setSharingQuest] = useState(null);
  const [shareEmail, setShareEmail] = useState("");
  const [shareLoading, setShareLoading] = useState(false);
  const [sharedUsers, setSharedUsers] = useState([]);
  const [shareStatus, setShareStatus] = useState(null);
  const [renamingBoard, setRenamingBoard] = useState(null);
  const [boardName, setBoardName] = useState("");
  const [renameError, setRenameError] = useState("");
  const [renameLoading, setRenameLoading] = useState(false);
  const [creatingBoard, setCreatingBoard] = useState(false);
  const { startCreate, checking: checkingCreationAccess, error: creationAccessError } = useCrmBoardCreationAccess({ lang, onAllowed: () => setCreatingBoard(true) });

  const handleBoardCreated = board => {
    setQuests(previous => [board, ...previous.filter(item => String(item._id) !== String(board._id))]);
    setCreatingBoard(false);
    navigate(`/${lang}/crm/board/${board._id}`);
  };

  const getAdImageUrl = (quest) => {
    if (quest.adInfo?.thumbnail && quest.adInfo?.thumbnail !== "default.jpg") {
      return quest?.adInfo?.thumbnail;
    }
    if (quest?.adInfo?.visualElement) {
      return `${minioImgUrl}/${quest?.userId}/${quest?.adInfo?.visualElement}`;
    }
    return NoImg;
  };

  const getQuestName = (quest) => {
    return (
      quest?.name ||
      `${quest?.questionnaires?.[0]?.answer || ""} ${LimitCharacters(
        quest?.questionnaires?.[1]?.answer,
        10
      )} | ${LimitCharacters(quest?.Meta?.facebookPageName, 10)}`
    );
  };

  const cards = useMemo(() => {
    return (quests || []).map((quest) => {
      const questCountValue = quest.crmLeadCounts;
      return {
        quest,
        image: getAdImageUrl(quest),
        title: getQuestName(quest),
        pageName: LimitCharacters(quest.isWebsiteBoard ? quest.siteUrl || "" : quest?.Meta?.facebookPageName || "", 46),
        totalLeads: questCountValue ? getQuestLeadTotal(questCountValue) : '…',
        newCount: getQuestNewLeads(questCountValue),
        isOwner: String(quest?.userId || "") === String(currentUserId || ""),
      };
    });
  }, [currentUserId, quests]);

  const openSharePanel = async (quest) => {
    setSharingQuest(quest);
    setShareEmail("");
    setShareLoading(true);
    setShareStatus(null);

    const responseValue = await b.getBoardSharing(quest?._id);
    setSharedUsers(responseValue?.data?.sharedWith || []);
    setShareLoading(false);
  };

  const closeSharePanel = () => {
    setSharingQuest(null);
    setShareEmail("");
    setSharedUsers([]);
    setShareStatus(null);
    setShareLoading(false);
  };

  const saveWebsiteBoardName = async () => {
    if (renameLoading) return;
    const name = boardName.trim();
    if (!name || name.length > 120) {
      setRenameError(t("Enter a board name of at most 120 characters"));
      return;
    }
    setRenameLoading(true);
    setRenameError("");
    const result = await b.renameWebsiteBoard(renamingBoard?._id, name);
    setRenameLoading(false);
    if (!result || result.status >= 400 || !result.data?.success) {
      setRenameError(result?.data?.message || t("Could not rename board right now"));
      return;
    }
    setQuests((previous) => previous.map((quest) => String(quest._id) === String(renamingBoard._id)
      ? { ...quest, name: result.data.data.name }
      : quest));
    setRenamingBoard(null);
  };

  const handleShareBoard = async () => {
    const normalizedEmail = String(shareEmail || "").trim().toLowerCase();
    if (!normalizedEmail) {
      setShareStatus({ type: "error", text: t("Enter an email to share this board") });
      return;
    }

    if (!isValidEmailAddress(normalizedEmail)) {
      setShareStatus({ type: "error", text: t("Enter a valid email address") });
      return;
    }

    setShareLoading(true);
    const responseValue = await b.shareBoard(sharingQuest?._id, normalizedEmail);
    const responseCode = String(responseValue?.data?.code || responseValue?.code || "");
    const hasErrorCode = /^(?:[A-Z]+)?(?:4|5)\d{3}$/.test(responseCode);

    if (responseValue === false) {
      setShareStatus({ type: "error", text: t("Could not share board right now") });
      setShareLoading(false);
      return;
    }

    const shareError = String(
      (hasErrorCode ? responseValue?.data?.msg : "") ||
      responseValue?.data?.error ||
      responseValue?.error ||
      responseValue?.message ||
      ""
    ).trim();

    if (shareError) {
      setShareStatus({ type: "error", text: shareError, code: responseCode });
      setShareLoading(false);
      return;
    }

    setSharedUsers(responseValue?.data?.sharedWith || []);
    setShareEmail("");
    setShareStatus({
      type: "success",
      text: responseCode === "B2003" ? t("Invitation sent") : t("Board shared successfully"),
    });
    setShareLoading(false);
  };

  const handleUnshareBoard = async (sharedValue) => {
    setShareLoading(true);
    const responseValue = await b.unshareBoard(
      sharingQuest?._id,
      sharedValue?.email,
      sharedValue?.userId
    );

    if (responseValue === false || responseValue?.data?.error) {
      setShareStatus({ type: "error", text: t("Could not remove access right now") });
      setShareLoading(false);
      return;
    }

    setSharedUsers(responseValue?.data?.sharedWith || []);
    setShareStatus({ type: "success", text: t("Access removed") });
    setShareLoading(false);
  };

  useEffect(() => {
    const trimmedSearchTerm = searchTerm.trim();

    if (!trimmedSearchTerm) {
      setSearchLoading(false);
      setSearchResults([]);
      setSearchError(false);
      return undefined;
    }

    let isMounted = true;
    const timeoutId = setTimeout(async () => {
      setSearchLoading(true);
      setSearchError(false);

      const responseValue = await b.searchUserLeads(trimmedSearchTerm);

      if (!isMounted) return;

      if (responseValue === false) {
        if (!isMounted) return;
        setSearchResults([]);
        setSearchError(true);
        setSearchLoading(false);
        return;
      }

      const nextResults = (responseValue?.data?.results || []).map((resultValue) => {
        const leadValue = resultValue?.lead || {};

        return {
          leadName: getMatchLeadName(leadValue) || trimmedSearchTerm,
          email: getMatchLeadEmail(leadValue),
          phone: getMatchLeadPhone(leadValue),
          boardName: resultValue?.boardName || "",
          questId: resultValue?.questId || "",
          columnTitle: resultValue?.columnTitle || "",
        };
      });

      setSearchResults(nextResults);

      setSearchLoading(false);
    }, 350);

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [searchTerm]);

  return (
    <PageFull>
      {creatingBoard ? (
        <CreateBoardPanel lang={lang} onCreated={handleBoardCreated} onClose={() => setCreatingBoard(false)} />
      ) : null}
      {sharingQuest ? (
        <Slider_
          title={t("Share board")}
          text={shareLoading ? t("Sharing...") : t("Share")}
          onClick={handleShareBoard}
          onClose={closeSharePanel}
          grey={t("Close")}
          onGrey={closeSharePanel}
        >
          <p>{t("Share")} <strong>{sharingQuest?.name || ""}</strong> {t("by email")}</p>
          <ShareInputState statusType={shareStatus?.type}>
            <Input_
              placeholder={t("Enter user email")}
              value={shareEmail}
              onChange={(e) => {
                setShareEmail(e.target.value);
                if (shareStatus?.type === "error" || shareStatus?.type === "warning") setShareStatus(null);
              }}
            />
          </ShareInputState>

          <ShareMessage statusType={shareStatus?.type}>
            {shareStatus?.text || null}
          </ShareMessage>

          {shareLoading ? <Partial_Loader /> : null}

          <ShareList>
            {sharedUsers.map((sharedValue) => (
              <ShareRow key={`${sharedValue?.userId || sharedValue?.email}`}>
                <div>
                  <strong>{sharedValue?.email}</strong>
                  <span>{sharedValue?.pending ? t("Invitation sent") : t("Has access to this board")}</span>
                </div>
                <Grey_Link
                  text={t("Remove")}
                  noPadding
                  size="small"
                  onClick={() => handleUnshareBoard(sharedValue)}
                />
              </ShareRow>
            ))}
          </ShareList>
        </Slider_>
      ) : null}

      {renamingBoard ? (
        <Slider_
          title={t("Rename website board")}
          text={renameLoading ? t("Saving...") : t("Save")}
          onClick={saveWebsiteBoardName}
          onClose={() => setRenamingBoard(null)}
          grey={t("Close")}
          onGrey={() => setRenamingBoard(null)}
        >
          <p>{renamingBoard.siteUrl}</p>
          <Input_
            value={boardName}
            maxLength={120}
            onChange={(event) => { setBoardName(event.target.value); setRenameError(""); }}
            placeholder={t("Board name")}
          />
          {renameError ? <ShareMessage statusType="error">{renameError}</ShareMessage> : null}
        </Slider_>
      ) : null}

      <PageTitle cta>
        <h1>{t("Boards")}</h1>
        <Main_ text={checkingCreationAccess ? t('Checking...') : t('Create board')} onClick={startCreate} />
      </PageTitle>

      {loading && <Partial_Loader />}
      {creationAccessError ? <SearchFeedback error role="alert">
        <p>{t(creationAccessError)}</p><Grey_Link text={t('Retry')} onClick={startCreate} />
      </SearchFeedback> : null}
      {loadError ? <SearchFeedback error role="alert">
        <p>{t(loadError)}</p>
        <Grey_Link text={t('Retry')} onClick={refreshBoards} />
      </SearchFeedback> : null}
      {availabilityWarning && !loadError ? <SearchFeedback role="status"><p>{t(availabilityWarning)}</p></SearchFeedback> : null}

      {!loading && (
        <BoardsWrap>
          {cards.length > 0 && <SearchWrap>
            <Input_
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={t("Search for a lead")}
            />

            <SearchFeedback error={searchError}>
              <p>{searchLoading
                ? t("Searching...")
                : searchError
                ? t("Could not search leads right now")
                : searchTerm.trim() && !searchResults.length
                ? t("No leads found")
                : null}</p>
            </SearchFeedback>

            {searchResults.length ? (
              <SearchResults>
                {searchResults.map((resultValue, index) => (
                  <SearchResultButton
                    key={`${resultValue.questId}-${resultValue.leadName}-${index}`}
                    onClick={() => navigate(`/${lang}/crm/board/${resultValue.questId}`)}
                  >
                    <p>
                      {t("Found")} <strong>"{resultValue.leadName}"</strong> {t("in board")}{" "}
                      <strong>"{resultValue.boardName}"</strong>
                    </p>
                    <span>
                      {[
                        resultValue.email,
                        resultValue.phone,
                        resultValue.columnTitle,
                      ]
                        .filter(Boolean)
                        .join(" • ")}
                    </span>
                  </SearchResultButton>
                ))}
              </SearchResults>
            ) : null}
          </SearchWrap>}

          {cards.length ? (
            <BoardsList>
              {cards.map(({ quest, image, title, pageName, totalLeads, newCount, isOwner }) => (
                <BoardCard
                  key={quest._id}
                  onClick={() => navigate(`/${lang}/crm/board/${quest._id}`)}
                >
                  <BoardTop>
                    <BoardImage>
                      {quest.isStandaloneBoard || quest.isWebsiteBoard ? (
                        <img src={getBoardAvatarSrc(quest)} alt={title} />
                      ) : <AdBoardImage key={`${image}:${quest.userId}:${quest.adInfo?.visualElement}`}
                        image={image} visualElement={quest.adInfo?.visualElement} userId={quest.userId} title={title} />}
                    </BoardImage>

                    <BoardContent>
                      <BoardHeader>
                        <h4>{title}</h4>
                        {pageName ? <p>{pageName}</p> : null}
                      </BoardHeader>
                    </BoardContent>

                    <BoardArrow>
                      <IconSvg Icon="Back" />
                    </BoardArrow>
                  </BoardTop>

                  <BoardMeta>
                    <span>
                      <IconSvg Icon="User" />
                      {t("{{count}} leads in pipeline", { count: totalLeads })}
                    </span>
                  </BoardMeta>

                  <BoardFooter>
                    <StatusRow>
                      <StatusPill highlight={newCount !== 0}>
                        {`+${newCount} ${t("new")}`}
                      </StatusPill>
                    </StatusRow>

                    {isOwner ? (
                      <CardActions>
                        {quest.isWebsiteBoard ? (
                          <CardActionButton type="button" onClick={(event) => {
                            event.stopPropagation();
                            setRenamingBoard(quest);
                            setBoardName(quest.name || "");
                            setRenameError("");
                          }}>
                            {t("Rename")}
                          </CardActionButton>
                        ) : null}
                        <CardActionButton
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openSharePanel(quest);
                          }}
                        >
                          {t("Share")}
                        </CardActionButton>
                      </CardActions>
                    ) : (
                      <ShareBadge>{t("Shared with you")}</ShareBadge>
                    )}
                  </BoardFooter>
                </BoardCard>
              ))}
            </BoardsList>
          ) : !loadError ? (
            <EmptyState aria-labelledby="crm-empty-boards-title">
              <EmptyStateIcon aria-hidden="true"><IconSvg Icon="Workspace" /></EmptyStateIcon>
              <h2 id="crm-empty-boards-title">{t("No boards available")}</h2>
              <p>{t('Create your first board to organize your contacts.')}</p>
              <EmptyStateCreate
                noPadding
                text={checkingCreationAccess ? t('Checking...') : t('Create board')}
                disabled={checkingCreationAccess}
                onClick={startCreate}
              />
            </EmptyState>
          ) : null}
        </BoardsWrap>
      )}
    </PageFull>
  );
}
